import { supabase } from '@/lib/integrations/supabase'
import type { AssetHolding, CreateAssetHoldingInput, UpdateAssetHoldingInput } from './holdingTypes'

export interface AssetGroupRef {
  id: string
  key: string
  category_id: string | null
}

export interface AssetOption {
  id: string
  name: string
  uom: string
  template_id?: string | null
  subgroup_id?: string | null
  attrs?: Record<string, any> | null
}

export interface AssetTemplateOption {
  id: string
  name: string
  default_uom: string | null
  subgroup_id?: string | null
  attrs?: Record<string, any> | null
  suggested_price?: number | null
}

export interface AssetSubgroupOption {
  id: string
  group_id: string
  name: string
}

export interface ListAssetHoldingsFilters {
  unitId: string
  assetIds?: string[]
  groupId?: string
  active?: boolean
}

export async function getAssetGroupByContextKey(
  contextKey: string,
  groupKey: string,
): Promise<AssetGroupRef> {
  const { data: context, error: contextError } = await supabase
    .from('contexts')
    .select('id')
    .eq('key', contextKey)
    .single()

  if (contextError || !context) {
    throw contextError ?? new Error(`Context not found: ${contextKey}`)
  }

  const { data: group, error: groupError } = await supabase
    .from('asset_groups')
    .select('id, key, category_id')
    .eq('context_id', context.id)
    .eq('key', groupKey)
    .single()

  if (groupError || !group) {
    throw groupError ?? new Error(`Asset group not found: ${contextKey}.${groupKey}`)
  }

  return group as AssetGroupRef
}

export async function listAssetsByGroup(
  unitId: string,
  groupId: string,
): Promise<AssetOption[]> {
  const { data, error } = await supabase
    .from('assets')
    .select('id, name, uom, template_id, subgroup_id, attrs')
    .eq('unit_id', unitId)
    .eq('group_id', groupId)
    .eq('active', true)
    .order('name', { ascending: true })

  if (error) throw error
  return (data ?? []) as AssetOption[]
}

export async function listAssetSubgroupsByGroup(groupId: string): Promise<AssetSubgroupOption[]> {
  const { data, error } = await supabase
    .from('asset_subgroups')
    .select('id, group_id, name')
    .eq('group_id', groupId)
    .order('name', { ascending: true })

  if (error) throw error
  return (data ?? []) as AssetSubgroupOption[]
}

export async function listAssetTemplatesByGroupKey(
  groupKey: string,
): Promise<AssetTemplateOption[]> {
  const { data, error } = await supabase
    .from('asset_templates')
    .select('id, name, default_uom, subgroup_id, attrs, suggested_price')
    .eq('group_key', groupKey)
    .eq('active', true)
    .order('name', { ascending: true })

  if (error) throw error
  return (data ?? []) as AssetTemplateOption[]
}

export async function createAssetFromTemplate({
  unitId,
  templateId,
  groupId,
}: {
  unitId: string
  templateId: string
  groupId: string
}): Promise<AssetOption> {
  const { data: template, error: templateError } = await supabase
    .from('asset_templates')
    .select('id, name, default_uom, subgroup_id, attrs, suggested_price')
    .eq('id', templateId)
    .single()

  if (templateError || !template) {
    throw templateError ?? new Error('Asset template not found')
  }

  const { data, error } = await supabase
    .from('assets')
    .insert({
      unit_id: unitId,
      template_id: template.id,
      name: template.name,
      uom: template.default_uom ?? 'un',
      price: template.suggested_price ?? null,
      group_id: groupId,
      subgroup_id: template.subgroup_id ?? null,
      active: true,
      attrs: template.attrs ?? {},
    })
    .select('id, name, uom, template_id, subgroup_id, attrs')
    .single()

  if (error || !data) throw error ?? new Error('Failed to create asset from template')
  return data as AssetOption
}

export async function listAssetHoldings({
  unitId,
  assetIds,
  groupId,
  active = true,
}: ListAssetHoldingsFilters): Promise<AssetHolding[]> {
  let resolvedAssetIds = assetIds

  if (!resolvedAssetIds && groupId) {
    const assets = await listAssetsByGroup(unitId, groupId)
    resolvedAssetIds = assets.map((asset) => asset.id)
  }

  if (resolvedAssetIds && resolvedAssetIds.length === 0) return []

  let query = supabase
    .from('asset_holdings')
    .select(`
      *,
      asset:assets!asset_holdings_asset_id_fkey (
        id,
        name,
        uom,
        group_id,
        subgroup_id,
        subgroup:asset_subgroups!assets_subgroup_id_fkey (id, name)
      ),
      party:parties!asset_holdings_party_id_fkey (id, name),
      location:asset_holding_locations!asset_holdings_location_id_fkey (id, name, description)
    `)
    .eq('unit_id', unitId)
    .order('created_at', { ascending: false })

  if (active !== undefined) query = query.eq('active', active)
  if (resolvedAssetIds) query = query.in('asset_id', resolvedAssetIds)

  const { data, error } = await query
  if (error) throw error
  return (data ?? []) as AssetHolding[]
}

export async function getAssetHolding(id: string): Promise<AssetHolding> {
  const { data, error } = await supabase
    .from('asset_holdings')
    .select(`
      *,
      asset:assets!asset_holdings_asset_id_fkey (
        id,
        name,
        uom,
        group_id,
        subgroup_id,
        subgroup:asset_subgroups!assets_subgroup_id_fkey (id, name)
      ),
      party:parties!asset_holdings_party_id_fkey (id, name),
      location:asset_holding_locations!asset_holdings_location_id_fkey (id, name, description)
    `)
    .eq('id', id)
    .single()

  if (error || !data) throw error ?? new Error('Asset holding not found')
  return data as AssetHolding
}

export async function createAssetHolding(
  unitId: string,
  input: CreateAssetHoldingInput,
): Promise<AssetHolding> {
  const { data, error } = await supabase
    .from('asset_holdings')
    .insert({
      unit_id: unitId,
      asset_id: input.asset_id,
      party_id: input.party_id ?? null,
      location_id: input.location_id ?? null,
      name: input.name ?? null,
      quantity: input.initial_quantity,
      initial_quantity: input.initial_quantity,
      uom: input.uom ?? 'un',
      acquired_at: input.acquired_at ?? null,
      started_at: input.started_at ?? null,
      unit_cost: input.unit_cost ?? null,
      total_cost: input.total_cost ?? null,
      attrs: input.attrs ?? {},
    })
    .select()
    .single()

  if (error || !data) throw error ?? new Error('Failed to create asset holding')
  return data as AssetHolding
}

export async function updateAssetHolding(
  id: string,
  input: UpdateAssetHoldingInput,
): Promise<AssetHolding> {
  const { data, error } = await supabase
    .from('asset_holdings')
    .update(input)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data as AssetHolding
}

export async function softDeleteAssetHolding(id: string): Promise<void> {
  const { error } = await supabase
    .from('asset_holdings')
    .update({ active: false, status: 'inactive' })
    .eq('id', id)

  if (error) throw error
}

export async function hardDeleteAssetHolding(id: string): Promise<void> {
  const { error } = await supabase
    .from('asset_holdings')
    .delete()
    .eq('id', id)

  if (error) throw error
}

export { softDeleteAssetHolding as deleteAssetHolding }
